import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { authFetch } from "../api";
import Header from "../components/Header";
import ListingEditModal from "../components/ListingEditModal";

// Per-building Sponsor Deal page: profile + deal scorecard + active units + sold comps.
// Reached by clicking a building on SponsorDealsPage.
export default function SponsorBuildingPage() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editModalListing, setEditModalListing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      setData(await authFetch(`/listings/sponsor-deals/building/${propertyId}`));
    } catch (e) {
      console.error("Error loading sponsor building:", e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  const fmt = (v) => (v != null ? `$${Math.round(v).toLocaleString()}` : "—");
  const pct = (v) => (v != null ? `${v.toFixed(1)}%` : "—");
  const ownLabel = (o) => (o === "COOP" ? "Co-op" : o === "CONDP" ? "Condop" : o === "CONDO" ? "Condo" : o || "—");

  const th = { textAlign: "left", padding: "8px 10px", fontSize: 11, fontWeight: 700, color: "#555", borderBottom: "2px solid #e0e0e0", textTransform: "uppercase", whiteSpace: "nowrap" };
  const td = { padding: "8px 10px", fontSize: 13, color: "#333", borderBottom: "1px solid #eee", verticalAlign: "middle" };
  const actionBtn = (bg) => ({ padding: "3px 9px", fontSize: 11, backgroundColor: bg, color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" });
  const card = { background: "#fff", borderRadius: 8, boxShadow: "0 2px 4px rgba(0,0,0,0.08)", padding: "12px 16px", minWidth: 130 };
  const cardLabel = { fontSize: 11, color: "#888", textTransform: "uppercase", fontWeight: 700, marginBottom: 4 };
  const cardVal = { fontSize: 20, fontWeight: 700, color: "#222" };
  const sec = { background: "#fff", borderRadius: 8, boxShadow: "0 2px 4px rgba(0,0,0,0.1)", overflow: "auto", marginTop: 16 };

  const s = data?.scorecard;

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      <Header showLogout={true} />
      <div style={{ padding: "20px", maxWidth: "1500px", margin: "0 auto" }}>
        <button onClick={() => navigate("/sponsor-deals")}
          style={{ background: "none", border: "none", color: "#1976d2", cursor: "pointer", fontSize: 13, padding: 0, marginBottom: 10 }}>
          ‹ Back to Sponsor Deals
        </button>

        {loading ? (
          <div style={{ padding: 40, color: "#999" }}>Loading…</div>
        ) : !data ? (
          <div style={{ padding: 40, color: "#999" }}>Building not found.</div>
        ) : (
          <>
            {/* Profile */}
            <h1 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 2px 0", color: "#222" }}>{data.address}</h1>
            {data.building_name && <div style={{ fontSize: 15, color: "#1565c0", fontWeight: 600, marginBottom: 4 }}>{data.building_name}</div>}
            <div style={{ fontSize: 13, color: "#777", marginBottom: 16 }}>
              {[ownLabel(data.ownership_type), data.neighborhood,
                data.year_built ? `Built ${data.year_built}` : null,
                data.total_floors ? `${data.total_floors} floors` : null,
                data.total_units ? `${data.total_units} units` : null,
                data.building_tier ? `Tier ${data.building_tier}` : null,
              ].filter(Boolean).join("  ·  ")}
            </div>

            {/* Deal scorecard */}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <div style={card}>
                <div style={cardLabel}>Cap rate</div>
                <div style={cardVal}>{pct(s.avg_cap)} <span style={{ fontSize: 12, color: "#888", fontWeight: 500 }}>avg</span></div>
                <div style={{ fontSize: 12, color: "#666" }}>
                  best {pct(s.best_cap)}
                  {s.cap_vs_hood != null && (
                    <span style={{ color: s.cap_vs_hood >= 0 ? "#2e7d32" : "#c62828", fontWeight: 600 }}>
                      {"  "}{s.cap_vs_hood >= 0 ? "+" : ""}{s.cap_vs_hood} vs hood {pct(s.hood_median_cap)}
                    </span>
                  )}
                </div>
              </div>
              <div style={card}>
                <div style={cardLabel}>Avg $/sqft</div>
                <div style={cardVal}>{s.avg_ppsf != null ? `$${s.avg_ppsf.toLocaleString()}` : "—"}</div>
              </div>
              <div style={card}>
                <div style={cardLabel}>Sell-through</div>
                <div style={cardVal}>{s.sell_through_pct != null ? `${s.sell_through_pct}%` : "—"}</div>
                <div style={{ fontSize: 12, color: "#666" }}>
                  {s.active_count} active · {s.sold_count} sold{s.pending_count ? ` · ${s.pending_count} in contract` : ""}
                </div>
              </div>
              <div style={card}>
                <div style={cardLabel}>Avg DOM (active)</div>
                <div style={cardVal}>{s.avg_active_dom != null ? `${s.avg_active_dom}d` : "—"}</div>
              </div>
              <div style={card}>
                <div style={cardLabel}>Sold 30/60/90d</div>
                <div style={cardVal}>{s.sold_30d} / {s.sold_60d} / {s.sold_90d}</div>
              </div>
            </div>

            {/* Active units */}
            <div style={sec}>
              <div style={{ padding: "10px 14px", fontWeight: 700, color: "#222", fontSize: 14 }}>
                Active sponsor units ({data.active_units.length})
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>
                  <th style={th}>Unit</th><th style={th}>Bd/Ba/Sqft</th><th style={th}>Price</th>
                  <th style={th}>$/sqft</th><th style={th}>Pred. Rent</th><th style={th}>Cap</th>
                  <th style={th}>DOM</th><th style={th}>RP</th><th style={th}>Actions</th>
                </tr></thead>
                <tbody>
                  {data.active_units.length === 0 ? (
                    <tr><td colSpan={9} style={{ ...td, textAlign: "center", color: "#999", padding: 24 }}>No active sponsor units.</td></tr>
                  ) : data.active_units.map((u) => (
                    <tr key={u.id}>
                      <td style={{ ...td, fontWeight: 600 }}>{u.unit_number ? `#${u.unit_number}` : "—"}</td>
                      <td style={td}>{(u.bedrooms ?? "?")}bd · {(u.bathrooms ?? "?")}ba{u.sqft ? ` · ${u.sqft.toLocaleString()} sf` : ""}</td>
                      <td style={{ ...td, fontWeight: 600 }}>{fmt(u.list_price)}</td>
                      <td style={td}>{u.ppsf != null ? `$${u.ppsf.toLocaleString()}` : "—"}</td>
                      <td style={td}>{u.predicted_rent ? <span style={{ color: "#1976d2", fontWeight: 600 }}>{fmt(u.predicted_rent)}</span> : <span style={{ color: "#bbb" }}>—</span>}</td>
                      <td style={td}>{u.net_yield != null ? pct(u.net_yield) : <span style={{ color: "#bbb" }}>—</span>}</td>
                      <td style={td}>{u.active_dom != null ? `${u.active_dom}d` : "—"}</td>
                      <td style={td} title="RealPlus <sponsorunit> verification">
                        {u.sponsor_rp === "Y" ? <span style={{ color: "#2e7d32", fontWeight: 700 }}>✓</span>
                          : u.sponsor_rp === "N" ? <span style={{ color: "#c62828", fontWeight: 700 }}>!</span>
                          : <span style={{ color: "#ccc" }}>·</span>}
                      </td>
                      <td style={td}>
                        <div style={{ display: "flex", gap: 5 }}>
                          <button style={actionBtn("#1976d2")} onClick={() => navigate(`/predict/${u.id}`)}>Analyze</button>
                          <button style={actionBtn("#455a64")} onClick={() => setEditModalListing({ id: u.id })}>Edit</button>
                          {u.se_url && /^https?:\/\//i.test(u.se_url) && <button style={actionBtn("#6d4c41")} onClick={() => window.open(u.se_url, "_blank", "noopener")}>SE</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* In contract / pending — NOT sold yet, so asking price only (no sale price). */}
            {data.pending_units && data.pending_units.length > 0 && (
              <div style={sec}>
                <div style={{ padding: "10px 14px", fontWeight: 700, color: "#222", fontSize: 14 }}>
                  In contract / pending ({data.pending_units.length})
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr>
                    <th style={th}>Unit</th><th style={th}>Bd/Ba/Sqft</th><th style={th}>Asking</th>
                    <th style={th}>$/sqft</th><th style={th}>Status</th><th style={th}>Listed</th>
                  </tr></thead>
                  <tbody>
                    {data.pending_units.map((u) => (
                      <tr key={u.id}>
                        <td style={{ ...td, fontWeight: 600 }}>{u.unit_number ? `#${u.unit_number}` : "—"}</td>
                        <td style={td}>{(u.bedrooms ?? "?")}bd · {(u.bathrooms ?? "?")}ba{u.sqft ? ` · ${u.sqft.toLocaleString()} sf` : ""}</td>
                        <td style={{ ...td, fontWeight: 600 }}>{fmt(u.list_price)}</td>
                        <td style={td}>{u.ppsf != null ? `$${u.ppsf.toLocaleString()}` : "—"}</td>
                        <td style={td}><span style={{ fontSize: 11, fontWeight: 600, color: "#8a6d3b", background: "#fcf3d9", borderRadius: 4, padding: "1px 6px" }}>{u.listing_status}</span></td>
                        <td style={td}>{u.listed_at ? u.listed_at.slice(0, 10) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Sold comps — the sponsor's own pricing history (ask vs actual sale) */}
            {data.sold_units.length > 0 && (
              <div style={sec}>
                <div style={{ padding: "10px 14px", fontWeight: 700, color: "#222", fontSize: 14 }}>
                  Sold sponsor units — asking vs. actual sale ({data.sold_units.length})
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr>
                    <th style={th}>Unit</th><th style={th}>Bd/Ba/Sqft</th><th style={th}>Asking</th>
                    <th style={th}>Sale</th><th style={th}>Sale $/sqft</th><th style={th}>Sale vs Ask</th>
                    <th style={th}>Closed</th>
                  </tr></thead>
                  <tbody>
                    {data.sold_units.map((u) => (
                      <tr key={u.id} style={{ background: "#fbfbfb" }}>
                        <td style={{ ...td, fontWeight: 600 }}>{u.unit_number ? `#${u.unit_number}` : "—"}</td>
                        <td style={td}>{(u.bedrooms ?? "?")}bd · {(u.bathrooms ?? "?")}ba{u.sqft ? ` · ${u.sqft.toLocaleString()} sf` : ""}</td>
                        <td style={td}>{fmt(u.list_price)}</td>
                        <td style={{ ...td, fontWeight: 600 }}>{fmt(u.sale_price)}</td>
                        <td style={td}>{u.sale_ppsf != null ? `$${u.sale_ppsf.toLocaleString()}` : "—"}</td>
                        <td style={td}>
                          {u.sale_vs_ask_pct != null
                            ? <span style={{ color: u.sale_vs_ask_pct >= 0 ? "#2e7d32" : "#c62828", fontWeight: 600 }}>
                                {u.sale_vs_ask_pct >= 0 ? "+" : ""}{u.sale_vs_ask_pct}%
                              </span>
                            : "—"}
                        </td>
                        <td style={td}>{u.closing_date ? u.closing_date.slice(0, 10) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {editModalListing && (
        <ListingEditModal
          listing={editModalListing}
          onClose={() => setEditModalListing(null)}
          onSave={() => load()}   /* Save keeps the editor open; the X closes it */
          onVerify={() => { setEditModalListing(null); load(); }}
        />
      )}
    </div>
  );
}
